import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaCommentDots, FaEdit, FaWhatsapp } from "react-icons/fa";
import { apihubGet, apihubPost } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const TIMEZONES = [
  "Asia/Kolkata",
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Asia/Dubai",
  "Asia/Singapore",
  "Australia/Sydney",
];

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

const defaultSchedule = () =>
  Object.fromEntries(
    DAYS.map((day) => [day, { enabled: true, open: "09:00", close: "18:00" }]),
  );

const to12Parts = (hhmm) => {
  const [h = 0, m = 0] = (hhmm || "00:00").split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return { hour: hour12, minute: m, period };
};

const from12Parts = (hour, minute, period) => {
  let h24 = hour % 12;
  if (period === "PM") h24 += 12;
  return `${String(h24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const formatRange24 = (open, close) => `${open || "00:00"} - ${close || "00:00"}`;

const extractTemplatePreview = (prefs) => {
  const template = prefs?.template?.templateAssociated;
  if (!template?.components?.length) return null;
  const header = template.components.find((c) => c.type === "HEADER");
  const body = template.components.find((c) => c.type === "BODY");
  const buttons = template.components.find((c) => c.type === "BUTTONS");
  return {
    name: template.name,
    header: header?.text || "",
    body: body?.text || "",
    buttons: buttons?.buttons?.map((b) => b.text).filter(Boolean) || [],
  };
};

const Toggle = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className={`greeting-toggle${checked ? " is-on" : ""}`}
    onClick={() => onChange(!checked)}
    disabled={disabled}
  />
);

const MessagePreview = ({ preview }) => (
  <div className="greeting-preview">
    <FaWhatsapp className="greeting-preview-wa" aria-hidden />
    {preview ? (
      <div className="greeting-preview-body">
        {preview.header ? (
          <p className="greeting-preview-header">{preview.header}</p>
        ) : null}
        {preview.body ? (
          <p className="greeting-preview-text">{preview.body}</p>
        ) : (
          <p className="greeting-preview-empty">No message configured yet.</p>
        )}
        {preview.buttons?.length > 0 && (
          <div className="greeting-preview-buttons">
            {preview.buttons.map((text) => (
              <span key={text} className="greeting-preview-btn">
                {text}
              </span>
            ))}
          </div>
        )}
      </div>
    ) : (
      <p className="greeting-preview-empty">No message configured yet.</p>
    )}
  </div>
);

const MessageCard = ({
  title,
  hint,
  enabled,
  onToggle,
  onEdit,
  preview,
  saving,
}) => (
  <article className="greeting-message-card">
    <div className="greeting-message-card-head">
      <div>
        <h3>{title}</h3>
        <p>{hint}</p>
      </div>
      <div className="greeting-message-card-actions">
        <Toggle
          checked={enabled}
          onChange={onToggle}
          disabled={saving}
          label={`Toggle ${title}`}
        />
        <button
          type="button"
          className="greeting-edit-btn"
          onClick={onEdit}
          disabled={saving}
        >
          <FaEdit aria-hidden />
          Edit
        </button>
      </div>
    </div>
    <MessagePreview preview={preview} />
  </article>
);

const LiveChatSettings = () => {
  const { projectId } = useProjectPhone();
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [autoResolveChats, setAutoResolveChats] = useState(true);
  const [sendWelcomeMessage, setSendWelcomeMessage] = useState(false);
  const [sendOffHoursMessage, setSendOffHoursMessage] = useState(false);
  const [welcomePrefs, setWelcomePrefs] = useState(null);
  const [offHoursPrefs, setOffHoursPrefs] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const [hoursRes, welcomeRes] = await Promise.all([
        apihubGet(
          `/getInteractedCustomerDetail/getworkinghourspreferences/${projectId}`,
        ),
        apihubGet(
          `/getInteractedCustomerDetail/getwelcomeoffmessagepreferences/${projectId}`,
        ),
      ]);
      const prefs = hoursRes?.workingHoursPreferences;
      if (prefs?.timezone) setTimezone(prefs.timezone);
      if (prefs?.schedule) {
        setSchedule({ ...defaultSchedule(), ...prefs.schedule });
      }

      const welcome = welcomeRes?.welcomeAndOffHoursMessagePreferences || {};
      setAutoResolveChats(welcome.autoResolveChats !== false);
      setSendWelcomeMessage(Boolean(welcome.sendWelcomeMessage));
      setSendOffHoursMessage(Boolean(welcome.sendOffHoursMessage));
      setWelcomePrefs(welcome.welcomeMessagePreferences || null);
      setOffHoursPrefs(welcome.offHoursMessagePreferences || null);
    } catch (err) {
      setError(err.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const welcomePreview = useMemo(
    () => extractTemplatePreview(welcomePrefs),
    [welcomePrefs],
  );
  const offHoursPreview = useMemo(
    () => extractTemplatePreview(offHoursPrefs),
    [offHoursPrefs],
  );

  const saveWorkingHours = useCallback(
    async (nextTimezone, nextSchedule) => {
      if (!projectId) return;
      setSaving("hours");
      setError("");
      setSuccess("");
      try {
        await apihubPost(
          `/getInteractedCustomerDetail/saveworkinghourspreferences/${projectId}`,
          { timezone: nextTimezone, schedule: nextSchedule },
        );
        setSuccess("Working hours saved.");
      } catch (err) {
        setError(err.message || "Failed to save working hours");
      } finally {
        setSaving("");
      }
    },
    [projectId],
  );

  const patchWelcome = async (patch) => {
    if (!projectId) return;
    setSaving("welcome");
    setError("");
    setSuccess("");
    try {
      await apihubPost(
        `/getInteractedCustomerDetail/updatewelcomeoffmessagepreferences/${projectId}`,
        patch,
      );
      setSuccess("Greeting settings updated.");
    } catch (err) {
      setError(err.message || "Failed to update greeting settings");
    } finally {
      setSaving("");
    }
  };

  const onTimezoneChange = (value) => {
    setTimezone(value);
    saveWorkingHours(value, schedule);
  };

  const onDayChange = (day, field, value) => {
    setSchedule((prev) => {
      const next = {
        ...prev,
        [day]: { ...prev[day], [field]: value },
      };
      saveWorkingHours(timezone, next);
      return next;
    });
  };

  const onTimeChange = (day, field, part, value) => {
    const current = schedule[day]?.[field] || "09:00";
    const parts = to12Parts(current);
    if (part === "hour") parts.hour = Number(value);
    if (part === "minute") parts.minute = Number(value);
    if (part === "period") parts.period = value;
    onDayChange(day, field, from12Parts(parts.hour, parts.minute, parts.period));
  };

  if (loading) {
    return (
      <div className="greeting-page">
        <p className="greeting-loading">Loading greeting settings…</p>
      </div>
    );
  }

  const editPreview =
    editModal === "welcome" ? welcomePreview : editModal === "off" ? offHoursPreview : null;
  const editTitle =
    editModal === "welcome" ? "Welcome Message" : "Off Hours Message";

  return (
    <div className="greeting-page">
      <header className="greeting-header">
        <h1>
          <FaCommentDots aria-hidden />
          Greeting Message
        </h1>
        <p className="greeting-subtitle">
          You can configure the system to automatically resolve cases when a user
          has not responded for more than 24 hours, allowing you to close inactive
          conversations without manual intervention.
        </p>
      </header>

      {error && <p className="greeting-alert greeting-alert-error">{error}</p>}
      {success && (
        <p className="greeting-alert greeting-alert-success">{success}</p>
      )}

      <section className="greeting-auto-resolve">
        <div>
          <h2>Auto Resolve Chats</h2>
          <p>Disable auto resolve intervened chats.</p>
        </div>
        <Toggle
          checked={autoResolveChats}
          onChange={(next) => {
            setAutoResolveChats(next);
            patchWelcome({ autoResolveChats: next });
          }}
          disabled={saving === "welcome"}
          label="Auto resolve chats"
        />
      </section>

      <section className="greeting-messages-panel">
        <MessageCard
          title="Welcome Message"
          hint="Configure automated reply for user's first query during working hours."
          enabled={sendWelcomeMessage}
          onToggle={(next) => {
            setSendWelcomeMessage(next);
            patchWelcome({ sendWelcomeMessage: next });
          }}
          onEdit={() => setEditModal("welcome")}
          preview={welcomePreview}
          saving={saving === "welcome"}
        />
        <MessageCard
          title="Off Hours Message"
          hint="Configure automated reply for user's first query during off hours."
          enabled={sendOffHoursMessage}
          onToggle={(next) => {
            setSendOffHoursMessage(next);
            patchWelcome({ sendOffHoursMessage: next });
          }}
          onEdit={() => setEditModal("off")}
          preview={offHoursPreview}
          saving={saving === "welcome"}
        />
      </section>

      <section className="greeting-hours-card">
        <div className="greeting-hours-head">
          <h2>Working Hours</h2>
          <p>Configure day-wise working hours for automated replies.</p>
        </div>

        <label className="greeting-timezone-field">
          <span>Time Zone</span>
          <select
            value={timezone}
            onChange={(e) => onTimezoneChange(e.target.value)}
            disabled={saving === "hours"}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </label>

        <div className="greeting-schedule">
          {DAYS.map((day) => {
            const daySchedule = schedule[day] || {};
            const enabled = daySchedule.enabled !== false;
            const openParts = to12Parts(daySchedule.open || "09:00");
            const closeParts = to12Parts(daySchedule.close || "18:00");

            return (
              <div key={day} className="greeting-day-row">
                <span className="greeting-day-label">{day}</span>
                <div className="greeting-day-enabled">
                  <Toggle
                    checked={enabled}
                    onChange={(next) => onDayChange(day, "enabled", next)}
                    disabled={saving === "hours"}
                    label={`${day} enabled`}
                  />
                  <span>{enabled ? "Enabled" : "Disabled"}</span>
                </div>
                <div className="greeting-day-times">
                  <div className="greeting-time-selects">
                    <select
                      value={openParts.hour}
                      disabled={!enabled || saving === "hours"}
                      onChange={(e) =>
                        onTimeChange(day, "open", "hour", e.target.value)
                      }
                      aria-label={`${day} open hour`}
                    >
                      {HOURS_12.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                    <span>:</span>
                    <select
                      value={openParts.minute}
                      disabled={!enabled || saving === "hours"}
                      onChange={(e) =>
                        onTimeChange(day, "open", "minute", e.target.value)
                      }
                      aria-label={`${day} open minute`}
                    >
                      {MINUTES.map((m) => (
                        <option key={m} value={m}>
                          {String(m).padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <select
                      value={openParts.period}
                      disabled={!enabled || saving === "hours"}
                      onChange={(e) =>
                        onTimeChange(day, "open", "period", e.target.value)
                      }
                      aria-label={`${day} open period`}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                  <span className="greeting-time-sep">–</span>
                  <div className="greeting-time-selects">
                    <select
                      value={closeParts.hour}
                      disabled={!enabled || saving === "hours"}
                      onChange={(e) =>
                        onTimeChange(day, "close", "hour", e.target.value)
                      }
                      aria-label={`${day} close hour`}
                    >
                      {HOURS_12.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                    <span>:</span>
                    <select
                      value={closeParts.minute}
                      disabled={!enabled || saving === "hours"}
                      onChange={(e) =>
                        onTimeChange(day, "close", "minute", e.target.value)
                      }
                      aria-label={`${day} close minute`}
                    >
                      {MINUTES.map((m) => (
                        <option key={m} value={m}>
                          {String(m).padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <select
                      value={closeParts.period}
                      disabled={!enabled || saving === "hours"}
                      onChange={(e) =>
                        onTimeChange(day, "close", "period", e.target.value)
                      }
                      aria-label={`${day} close period`}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
                <span className="greeting-day-summary">
                  {enabled
                    ? formatRange24(daySchedule.open, daySchedule.close)
                    : "Closed"}
                </span>
              </div>
            );
          })}
        </div>
        {saving === "hours" && (
          <p className="greeting-hours-saving">Saving working hours…</p>
        )}
      </section>

      {editModal && (
        <div className="greeting-modal-backdrop" onClick={() => setEditModal(null)}>
          <div
            className="greeting-modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="greeting-edit-title"
          >
            <h3 id="greeting-edit-title">Edit {editTitle}</h3>
            <p className="greeting-modal-hint">
              Template preview for your automated reply. Full template editing is
              managed through your approved WhatsApp templates.
            </p>
            {editPreview?.name && (
              <p className="greeting-modal-template-name">
                Template: <strong>{editPreview.name}</strong>
              </p>
            )}
            <MessagePreview preview={editPreview} />
            <div className="greeting-modal-actions">
              <button
                type="button"
                className="greeting-modal-btn"
                onClick={() => setEditModal(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveChatSettings;
